import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { differenceInCalendarDays, parseISO, format } from "date-fns";
import { es } from "date-fns/locale";
import { api } from "../lib/api";
import {
  ESTADOS,
  ESTADOS_CAMBIABLES,
  FUENTES_CONSULTA,
  MOTIVOS_DESCARTE,
  CONTACT_METHODS,
  ESTADO_COLORS,
  getFuenteLabel,
  getMotivoDescarteLabel,
} from "../lib/constants";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Phone,
  Mail,
  User,
  FileText,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  CheckCircle2,
  Sparkles,
  Globe,
  Clock4,
  MessageCircle,
  XCircle,
  Send,
  Eye,
} from "lucide-react";



const parseEventDate = (value) => {
  if (!value || value === "no hay datos" || value === "No hay datos") return null;
  try {
    return parseISO(value);
  } catch (error) {
    const dateOnly = value.split("T")[0];
    try {
      return parseISO(dateOnly);
    } catch (err) {
      return null;
    }
  }
};

const formatDate = (value) => {
  if (!value || value === "no hay datos" || value === "No hay datos") return value;
  const date = parseEventDate(value);
  if (!date) return value;
  try {
    return format(date, "dd-MM-yyyy", { locale: es });
  } catch (error) {
    return value;
  }
};

const calculateDaysRemaining = (fecha_evento) => {
  const eventDate = parseEventDate(fecha_evento);
  if (!eventDate) return null;
  return differenceInCalendarDays(eventDate, new Date());
};

const formatDaysRemaining = (days) => {
  if (days === null || days === undefined) return null;
  if (days > 1) return `Faltan ${days} días`;
  if (days === 1) return "Falta 1 día";
  if (days === 0) return "Es hoy";
  if (days === -1) return "Fue ayer";
  return `Hace ${Math.abs(days)} días`;
};

export default function ConsultaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [consulta, setConsulta] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [contactForm, setContactForm] = useState({
    fecha_contacto: "",
    canal: "llamada",
    notas: "",
  });
  const [addingContact, setAddingContact] = useState(false);
  const [deletingContactIds, setDeletingContactIds] = useState({});
  const [showDescarteModal, setShowDescarteModal] = useState(false);
  const [motivoDescarte, setMotivoDescarte] = useState("");
  const [motivoDescarteOtro, setMotivoDescarteOtro] = useState("");

  // Email send state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [cancellingFollowup, setCancellingFollowup] = useState(false);

  // ── Email handlers ──────────────────────────────────────────────────────
  const handleOpenEmailModal = async () => {
    try {
      const res = await api.getEmailTemplates();
      setEmailTemplates(res.data);
      const defTemplateId = res.data.find(t => t.es_defecto)?.id || res.data[0]?.id || null;
      setSelectedTemplateId(defTemplateId);
      setEmailPreview(null);
      setShowEmailModal(true);
    } catch (e) {
      toast.error("Error al cargar plantillas de email");
    }
  };

  const handlePreviewEmail = async (templateId) => {
    setLoadingPreview(true);
    try {
      const res = await api.previewEmailConsulta(id, templateId);
      setEmailPreview(res.data);
    } catch (e) {
      toast.error("Error al previsualizar el email");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await api.enviarEmailConsulta(id, selectedTemplateId);
      if (res.data.success) {
        toast.success(res.data.message);
        setShowEmailModal(false);
        loadConsulta(); // Recargar para mostrar el nuevo log de contacto
      } else {
        toast.error(res.data.message || "Error al enviar el email");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el email. Revisa la configuración SMTP.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCancelFollowup = async () => {
    setCancellingFollowup(true);
    try {
      await api.updateConsulta(id, { fecha_proximo_contacto: "" });
      toast.success("Seguimiento cancelado");
      loadConsulta();
    } catch (e) {
      toast.error("Error al cancelar el seguimiento");
    } finally {
      setCancellingFollowup(false);
    }
  };
  // ────────────────────────────────────────────────────────────────────────

  const loadConsulta = useCallback(async () => {
    try {
      const response = await api.getConsulta(id);
      setConsulta(response.data);
      setFormData(response.data);
    } catch (error) {
      toast.error("Error al cargar la consulta");
      navigate("/consultas");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadTasks = useCallback(async () => {
    try {
      const response = await api.getTasks("consulta", id);
      setTasks(response.data);
    } catch (error) {
      console.error("Error loading tasks:", error);
    }
  }, [id]);

  useEffect(() => {
    loadConsulta();
    loadTasks();
  }, [id, loadConsulta, loadTasks]);

  const handleDeleteContacto = async (logId) => {
    if (!logId || deletingContactIds[logId]) return;
    const confirmed = window.confirm("¿Eliminar este registro de contacto?");
    if (!confirmed) return;
    setDeletingContactIds((prev) => ({ ...prev, [logId]: true }));
    try {
      await api.deleteConsultaContacto(id, logId);
      toast.success("Registro eliminado");
      loadConsulta();
    } catch (error) {
      toast.error("No se pudo eliminar el registro");
    } finally {
      setDeletingContactIds((prev) => {
        const next = { ...prev };
        delete next[logId];
        return next;
      });
    }
  };

  const diasRestantes = useMemo(() => {
    if (!consulta) return null;
    if (typeof consulta.dias_restantes === "number") return consulta.dias_restantes;
    return calculateDaysRemaining(consulta.fecha_evento);
  }, [consulta]);

  const handleAddContacto = async () => {
    if (!contactForm.canal || addingContact) return;
    setAddingContact(true);
    try {
      await api.addConsultaContacto(id, contactForm);
      toast.success("Contacto registrado");
      setContactForm({ fecha_contacto: "", canal: "llamada", notas: "" });
      loadConsulta();
    } catch (error) {
      toast.error("Error al registrar contacto");
    } finally {
      setAddingContact(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.updateConsulta(id, formData);
      setConsulta(response.data);
      setEditing(false);
      toast.success("Consulta actualizada");
      loadTasks(); // Reload tasks in case estado changed
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteConsulta(id);
      toast.success("Consulta eliminada");
      navigate("/consultas");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "No se puede eliminar esta consulta");
      } else {
        toast.error("Error al eliminar consulta");
      }
    }
  };

  const handleConvert = async () => {
    try {
      const response = await api.convertirConsulta(id);
      toast.success("¡Consulta convertida a cliente!");
      navigate(`/clientes/${response.data.id}`);
    } catch (error) {
      toast.error("Error al convertir");
    }
  };

  const handleChangeEstado = async (nuevoEstado) => {
    if (nuevoEstado === consulta.estado) return;

    if (nuevoEstado === "descartada") {
      setShowDescarteModal(true);
      return;
    }

    try {
      await api.updateConsulta(id, { estado: nuevoEstado });
      toast.success(`Estado actualizado a ${ESTADOS.find(e => e.value === nuevoEstado)?.label}`);
      loadConsulta();
      loadTasks();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleDescartar = async () => {
    if (!motivoDescarte) {
      toast.error("Selecciona un motivo de descarte");
      return;
    }

    if (motivoDescarte === "otro" && !motivoDescarteOtro.trim()) {
      toast.error("Especifica el motivo");
      return;
    }

    try {
      const motivoFinal = motivoDescarte === "otro" ? motivoDescarteOtro.trim() : motivoDescarte;
      await api.updateConsulta(id, {
        estado: "descartada",
        motivo_descarte: motivoFinal
      });
      toast.success("Consulta descartada");
      setShowDescarteModal(false);
      setMotivoDescarte("");
      setMotivoDescarteOtro("");
      loadConsulta();
      loadTasks();
    } catch (error) {
      toast.error("Error al descartar consulta");
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await api.createTask({
        titulo: newTaskTitle,
        entidad_tipo: "consulta",
        entidad_id: id,
      });
      setNewTaskTitle("");
      loadTasks();
      toast.success("Tarea añadida");
    } catch (error) {
      toast.error("Error al añadir tarea");
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await api.updateTask(task.id, { completada: !task.completada });
      loadTasks();
    } catch (error) {
      toast.error("Error al actualizar tarea");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.deleteTask(taskId);
      loadTasks();
      toast.success("Tarea eliminada");
    } catch (error) {
      toast.error("Error al eliminar tarea");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-primary font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  const pendingTasks = tasks.filter((t) => !t.completada);
  const completedTasks = tasks.filter((t) => t.completada);

  return (
    <div data-testid="consulta-detail-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/consultas")}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
                {consulta.nombre}
              </h1>
              <Badge className={ESTADO_COLORS[consulta.estado]} variant="outline">
                {ESTADOS.find((e) => e.value === consulta.estado)?.label}
              </Badge>
              {diasRestantes !== null && (
                <Badge variant="secondary" className="gap-2">
                  <Clock4 className="w-3 h-3" />
                  {formatDaysRemaining(diasRestantes)}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1">{consulta.tipo_evento}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Dropdown cambio de estado - no disponible si está convertida */}
          {consulta.estado !== "convertida" && (
            <Select
              value={consulta.estado}
              onValueChange={handleChangeEstado}
            >
              <SelectTrigger data-testid="change-estado-select" className="w-48 h-10 rounded-full border-secondary/50">
                <SelectValue placeholder="Cambiar estado" />
              </SelectTrigger>
              <SelectContent>
                {ESTADOS_CAMBIABLES.map((estadoValue) => (
                  <SelectItem
                    key={estadoValue}
                    value={estadoValue}
                    disabled={estadoValue === consulta.estado}
                  >
                    {ESTADOS.find(e => e.value === estadoValue)?.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Botón convertir a cliente - siempre visible si no está convertida */}
          {consulta.estado !== "convertida" && consulta.estado !== "descartada" && (
            <Button
              data-testid="convert-btn"
              onClick={handleConvert}
              className="rounded-full bg-secondary hover:bg-secondary/90 gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Convertir a Cliente
            </Button>
          )}
          {!editing ? (
            <Button
              variant="outline"
              data-testid="edit-btn"
              onClick={() => setEditing(true)}
              className="rounded-full gap-2"
            >
              <Edit className="w-4 h-4" />
              Editar
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setFormData(consulta);
                }}
                className="rounded-full gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
              <Button
                data-testid="save-btn"
                onClick={handleSave}
                className="rounded-full bg-primary gap-2"
                disabled={saving}
              >
                <Save className="w-4 h-4" />
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar consulta?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Se eliminarán todos los datos y tareas asociadas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="confirm-delete-btn"
                  onClick={handleDelete}
                  className="rounded-full bg-destructive"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Info Card */}
        <Card className="lg:col-span-2 border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Información de la Consulta</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {editing ? (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fuente</Label>
                    <Select
                      value={formData.fuente || "web"}
                      onValueChange={(value) => setFormData({ ...formData, fuente: value })}
                    >
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue placeholder="Seleccionar fuente" />
                      </SelectTrigger>
                      <SelectContent>
                        {FUENTES_CONSULTA.map((fuente) => (
                          <SelectItem key={fuente.value} value={fuente.value}>
                            {fuente.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <Select
                      value={formData.estado}
                      onValueChange={(value) => setFormData({ ...formData, estado: value })}
                    >
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTADOS.map((estado) => (
                          <SelectItem key={estado.value} value={estado.value}>
                            {estado.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Evento</Label>
                    <Input
                      type="date"
                      value={formData.fecha_evento}
                      onChange={(e) => setFormData({ ...formData, fecha_evento: e.target.value })}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Próximo contacto</Label>
                    <Input
                      type="date"
                      value={formData.fecha_proximo_contacto || ""}
                      onChange={(e) => setFormData({ ...formData, fecha_proximo_contacto: e.target.value })}
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Evento</Label>
                    <Select
                      value={formData.tipo_evento}
                      onValueChange={(value) => setFormData({ ...formData, tipo_evento: value })}
                    >
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["Boda", "Cumpleaños", "Evento Corporativo", "Comunión", "Graduación", "Fiesta Privada", "Otro"].map((tipo) => (
                          <SelectItem key={tipo} value={tipo}>
                            {tipo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ubicación</Label>
                    <Input
                      value={formData.ubicacion}
                      onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                      className="h-11 rounded-xl"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Textarea
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    className="rounded-xl resize-none"
                    rows={4}
                  />
                </div>
              </>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <User className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Nombre</p>
                    <p className="font-medium text-primary">{consulta.nombre}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Phone className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Teléfono</p>
                    <p className="font-medium text-primary">{consulta.telefono}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Mail className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium text-primary">{consulta.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Globe className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Fuente</p>
                    <p className="font-medium text-primary">
                      {FUENTES_CONSULTA.find(f => f.value === consulta.fuente)?.label || consulta.fuente}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Calendar className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Fecha Evento</p>
                    <p className="font-medium text-primary">{formatDate(consulta.fecha_evento)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Clock4 className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Próximo contacto</p>
                    <p className="font-medium text-primary">
                      {consulta.fecha_proximo_contacto ? formatDate(consulta.fecha_proximo_contacto) : "No programado"}
                    </p>
                    {diasRestantes !== null && (
                      <p className="text-xs text-muted-foreground">
                        {formatDaysRemaining(diasRestantes)}
                      </p>
                    )}
                    {consulta.fecha_proximo_contacto && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1 rounded-full"
                          onClick={handleOpenEmailModal}
                        >
                          <Send className="w-3 h-3" /> Enviar ahora
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs gap-1 rounded-full text-muted-foreground hover:text-destructive"
                          disabled={cancellingFollowup}
                          onClick={handleCancelFollowup}
                        >
                          <X className="w-3 h-3" /> Cancelar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:col-span-2">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <MapPin className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ubicación</p>
                    <p className="font-medium text-primary">{consulta.ubicacion}</p>
                  </div>
                </div>
                {consulta.estado === "descartada" && consulta.motivo_descarte && (
                  <div className="flex items-start gap-3 sm:col-span-2">
                    <div className="p-2 bg-red-10 rounded-lg">
                      <XCircle className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Motivo de descarte</p>
                      <p className="font-medium text-red-600">
                        {MOTIVOS_DESCARTE.find(m => m.value === consulta.motivo_descarte)?.label || consulta.motivo_descarte}
                      </p>
                    </div>
                  </div>
                )}
                {consulta.notas && (
                  <div className="flex items-start gap-3 sm:col-span-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <FileText className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Notas</p>
                      <p className="font-medium text-primary whitespace-pre-wrap">{consulta.notas}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tasks Card */}
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center justify-between">
              <span>Tareas</span>
              <Badge variant="outline">{pendingTasks.length} pendientes</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Task */}
            <div className="flex gap-2">
              <Input
                data-testid="new-task-input"
                placeholder="Nueva tarea..."
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddTask()}
                className="h-10 rounded-xl flex-1"
              />
              <Button
                size="icon"
                data-testid="add-task-btn"
                onClick={handleAddTask}
                className="rounded-xl h-10 w-10"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Pending Tasks */}
            <div className="space-y-2">
              {pendingTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl group"
                >
                  <Checkbox
                    data-testid={`task-checkbox-${task.id}`}
                    checked={task.completada}
                    onCheckedChange={() => handleToggleTask(task)}
                  />
                  <span className="flex-1 text-sm">{task.titulo}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteTask(task.id)}
                    className="opacity-0 group-hover:opacity-100 h-7 w-7 text-muted-foreground hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Completed Tasks */}
            {completedTasks.length > 0 && (
              <div className="pt-4 border-t border-border/40">
                <p className="text-sm text-muted-foreground mb-2">Completadas</p>
                <div className="space-y-2">
                  {completedTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 p-3 bg-green-50 rounded-xl group"
                    >
                      <Checkbox
                        checked={task.completada}
                        onCheckedChange={() => handleToggleTask(task)}
                      />
                      <span className="flex-1 text-sm text-muted-foreground line-through">
                        {task.titulo}
                      </span>
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteTask(task.id)}
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tasks.length === 0 && (
              <p className="text-center text-muted-foreground text-sm py-4">
                No hay tareas
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact log */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              Historial de contactos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3 items-end">
              <div className="space-y-2">
                <Label>Método</Label>
                <Select
                  value={contactForm.canal}
                  onValueChange={(value) => setContactForm({ ...contactForm, canal: value })}
                >
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha de contacto</Label>
                <Input
                  type="datetime-local"
                  value={contactForm.fecha_contacto}
                  onChange={(e) => setContactForm({ ...contactForm, fecha_contacto: e.target.value })}
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="flex flex-col md:col-span-1 gap-2">
                <Label>Notas</Label>
                <Textarea
                  value={contactForm.notas}
                  onChange={(e) => setContactForm({ ...contactForm, notas: e.target.value })}
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleAddContacto}
                disabled={addingContact}
                className="rounded-full"
              >
                {addingContact ? "Guardando..." : "Registrar contacto"}
              </Button>
            </div>

            <div className="space-y-3">
              {(consulta.contacto_logs || []).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Aún no hay interacciones registradas.
                </p>
              )}
              {(consulta.contacto_logs || [])
                .slice()
                .sort((a, b) => new Date(b.fecha_contacto) - new Date(a.fecha_contacto))
                .map((log) => (
                  <div key={log.id} className="p-4 rounded-2xl border border-border/60 space-y-1">
                    <div className="flex items-center justify-between text-sm gap-2">
                      <div className="flex items-center gap-2 font-medium text-primary">
                        <MessageCircle className="w-4 h-4" />
                        {CONTACT_METHODS.find((m) => m.value === log.canal)?.label || log.canal}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-xs">
                          {log.fecha_contacto
                            ? format(parseEventDate(log.fecha_contacto) || new Date(log.fecha_contacto), "d MMM yyyy - HH:mm", { locale: es })
                            : "Sin fecha"}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteContacto(log.id)}
                          disabled={!!deletingContactIds[log.id]}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    {log.notas && (
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{log.notas}</p>
                    )}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Resumen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10">
              <p className="text-sm text-muted-foreground">Días restantes</p>
              <p className="text-2xl font-heading text-primary">
                {diasRestantes !== null ? formatDaysRemaining(diasRestantes) : "Sin fecha"}
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-muted/40 border border-border/30 space-y-1">
              <p className="text-sm text-muted-foreground">Próximo contacto</p>
              <p className="font-medium">
                {consulta.fecha_proximo_contacto ?
                  formatDate(consulta.fecha_proximo_contacto) :
                  "No programado"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Link to Cliente if converted */}
      {consulta.estado === "convertida" && (
        <Card className="border-0 shadow-soft bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-700">Consulta Convertida</p>
                  <p className="text-sm text-green-600">Este lead se ha convertido en cliente</p>
                </div>
              </div>
              <Link to="/clientes">
                <Button className="rounded-full bg-green-600 hover:bg-green-700">
                  Ver Clientes
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de motivo de descarte */}
      <Dialog open={showDescarteModal} onOpenChange={setShowDescarteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Motivo de Descarte</DialogTitle>
            <DialogDescription>
              Selecciona el motivo por el cual se descarta esta consulta
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Select value={motivoDescarte} onValueChange={setMotivoDescarte}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un motivo" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS_DESCARTE.map((motivo) => (
                    <SelectItem key={motivo.value} value={motivo.value}>
                      {motivo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {motivoDescarte === "otro" && (
              <div className="space-y-2">
                <Label>Especifica el motivo</Label>
                <Textarea
                  placeholder="Describe el motivo del descarte..."
                  value={motivoDescarteOtro}
                  onChange={(e) => setMotivoDescarteOtro(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDescarteModal(false);
                setMotivoDescarte("");
                setMotivoDescarteOtro("");
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleDescartar}>
              Descartar Consulta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Send Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" /> Enviar email de seguimiento
            </DialogTitle>
            <DialogDescription>
              Selecciona una plantilla para enviar a <strong>{consulta?.email}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Selector de plantilla */}
            {emailTemplates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>No hay plantillas configuradas.</p>
                <p className="text-sm mt-1">Ve a Configuración → Emails para crear una.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Plantilla de email</Label>
                <Select
                  value={selectedTemplateId || ""}
                  onValueChange={(val) => {
                    setSelectedTemplateId(val);
                    setEmailPreview(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una plantilla..." />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nombre} {t.es_defecto ? "⭐" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Preview */}
            {selectedTemplateId && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 rounded-full mb-3"
                  onClick={() => handlePreviewEmail(selectedTemplateId)}
                  disabled={loadingPreview}
                >
                  <Eye className="w-4 h-4" />
                  {loadingPreview ? "Cargando..." : "Ver preview del email"}
                </Button>

                {emailPreview && (
                  <div className="border border-border rounded-xl overflow-hidden">
                    <div className="bg-muted/50 px-4 py-2 border-b text-sm">
                      <span className="text-muted-foreground">Asunto: </span>
                      <strong>{emailPreview.subject}</strong>
                    </div>
                    <iframe
                      srcDoc={emailPreview.html}
                      title="Preview email"
                      className="w-full min-h-[280px] border-0"
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-border/40">
            <Button variant="ghost" onClick={() => setShowEmailModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !selectedTemplateId || emailTemplates.length === 0}
              className="gap-2"
            >
              <Send className="w-4 h-4" />
              {sendingEmail ? "Enviando..." : "Enviar email"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
